import fs from "node:fs/promises";
import path from "node:path";

import type { PDFOptions, Page } from "puppeteer-core";

import { browserManager } from "./browser/browserManager";
import { injectPrintStyles } from "./styles/printStyles";
import {
  PdfEngineError,
  type AssetLoadFailure,
  type NormalizedRenderPdfOptions,
  type RenderPdfFileResult,
  type RenderPdfOptions,
  type RenderPdfResult
} from "./types/types";
import { closePage } from "./utils/cleanup";
import { validateOptions } from "./utils/validateOptions";

type RenderPdfFileOptions = RenderPdfOptions & {
  output: "file";
  outputPath: string;
};

type RenderPdfBufferOptions = RenderPdfOptions & {
  output?: "buffer";
};

function cssLengthToPixels(length: string, fallback: number): number {
  const trimmed = length.trim().toLowerCase();
  const match = /^(-?\d+(?:\.\d+)?)(px|in|cm|mm|pt)?$/.exec(trimmed);

  if (!match) {
    return fallback;
  }

  const value = Number(match[1]);
  const unit = match[2] ?? "px";

  if (!Number.isFinite(value) || value <= 0) {
    return fallback;
  }

  switch (unit) {
    case "px":
      return Math.round(value);
    case "in":
      return Math.round(value * 96);
    case "cm":
      return Math.round((value * 96) / 2.54);
    case "mm":
      return Math.round((value * 96) / 25.4);
    case "pt":
      return Math.round((value * 96) / 72);
    default:
      return fallback;
  }
}

function isTimeoutError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  return error.name === "TimeoutError" || /timeout|timed out/i.test(error.message);
}

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function withTimeout<T>(
  promise: Promise<T>,
  timeout: number,
  timeoutMessage: string
): Promise<T> {
  let timeoutId: NodeJS.Timeout | undefined;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new PdfEngineError("TIMEOUT", timeoutMessage));
    }, timeout);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  });
}

async function ensureOutputPathWritable(outputPath: string): Promise<void> {
  const parent = path.dirname(outputPath);

  try {
    const parentStat = await fs.stat(parent);
    if (!parentStat.isDirectory()) {
      throw new PdfEngineError(
        "FILE_OUTPUT_FAILED",
        "The outputPath parent exists but is not a directory.",
        {
          details: {
            outputPath,
            parent
          }
        }
      );
    }
  } catch (error) {
    if (error instanceof PdfEngineError) {
      throw error;
    }

    throw new PdfEngineError(
      "FILE_OUTPUT_FAILED",
      "The outputPath parent directory does not exist or is not accessible.",
      {
        cause: error,
        details: {
          outputPath,
          parent
        }
      }
    );
  }

  try {
    const outputStat = await fs.stat(outputPath);
    if (outputStat.isDirectory()) {
      throw new PdfEngineError(
        "FILE_OUTPUT_FAILED",
        "outputPath points to a directory, not a PDF file path.",
        {
          details: {
            outputPath
          }
        }
      );
    }
  } catch (error) {
    const nodeError = error as NodeJS.ErrnoException;
    if (nodeError.code !== "ENOENT") {
      if (error instanceof PdfEngineError) {
        throw error;
      }

      throw new PdfEngineError(
        "FILE_OUTPUT_FAILED",
        "Unable to validate outputPath.",
        {
          cause: error,
          details: {
            outputPath
          }
        }
      );
    }
  }
}

function createPdfOptions(options: NormalizedRenderPdfOptions): PDFOptions {
  const pdfOptions: PDFOptions = {
    printBackground: true,
    preferCSSPageSize: true,
    width: options.size.width,
    height: options.size.height,
    scale: options.scale
  };

  if (options.landscape !== undefined) {
    pdfOptions.landscape = options.landscape;
  }

  if (options.margins) {
    pdfOptions.margin = {
      top: options.margins.top,
      right: options.margins.right,
      bottom: options.margins.bottom,
      left: options.margins.left
    };
  }

  if (options.output === "file" && options.outputPath) {
    pdfOptions.path = options.outputPath;
  }

  return pdfOptions;
}

async function waitForAssets(page: Page, timeout: number): Promise<AssetLoadFailure[]> {
  return withTimeout(
    page.evaluate(async () => {
      const failures: AssetLoadFailure[] = [];

      if ("fonts" in document) {
        await document.fonts.ready;
      }

      const images = Array.from(document.images);
      await Promise.all(
        images.map(
          (image) =>
            new Promise<void>((resolve) => {
              const src = image.currentSrc || image.src || "inline image";

              if (image.complete && image.naturalWidth > 0) {
                resolve();
                return;
              }

              const finish = () => {
                if (!image.complete || image.naturalWidth === 0) {
                  failures.push({
                    url: src,
                    errorText: "Image failed to load."
                  });
                }

                resolve();
              };

              image.addEventListener("load", finish, { once: true });
              image.addEventListener("error", finish, { once: true });

              if ("decode" in image) {
                image.decode().then(finish).catch(finish);
              }
            })
        )
      );

      return failures;
    }),
    timeout,
    "Timed out while waiting for images and fonts to finish loading."
  );
}

function trackAssetFailures(page: Page, failures: AssetLoadFailure[]): void {
  page.on("requestfailed", (request) => {
    const url = request.url();

    if (url === "about:blank" || url.startsWith("data:") || url.startsWith("blob:")) {
      return;
    }

    failures.push({
      url,
      errorText: request.failure()?.errorText ?? "Request failed."
    });
  });

  page.on("response", (response) => {
    const status = response.status();
    const url = response.url();

    if (
      status < 400 ||
      url === "about:blank" ||
      url.startsWith("data:") ||
      url.startsWith("blob:")
    ) {
      return;
    }

    failures.push({
      url,
      errorText: `HTTP ${status}`
    });
  });
}

async function renderWithBrowser(options: NormalizedRenderPdfOptions): Promise<RenderPdfResult> {
  const browser = await browserManager.getBrowser(
    options.executablePath ? { executablePath: options.executablePath } : {}
  );
  let page: Page | undefined;
  const requestFailures: AssetLoadFailure[] = [];

  try {
    page = await browser.newPage();
    page.setDefaultTimeout(options.timeout);
    page.setDefaultNavigationTimeout(options.timeout);
    trackAssetFailures(page, requestFailures);

    await page.setViewport({
      width: cssLengthToPixels(options.size.width, 794),
      height: cssLengthToPixels(options.size.height, 1123),
      deviceScaleFactor: 2
    });

    await page.setContent(injectPrintStyles(options.html), {
      waitUntil: "networkidle0",
      timeout: options.timeout
    });

    const imageFailures = await waitForAssets(page, options.timeout);
    const assetFailures = [...requestFailures, ...imageFailures];

    if (assetFailures.length > 0) {
      throw new PdfEngineError(
        "ASSET_LOAD_FAILED",
        "One or more PDF assets failed to load.",
        {
          details: {
            assetFailures
          }
        }
      );
    }

    if (options.output === "file" && options.outputPath) {
      await ensureOutputPathWritable(options.outputPath);
    }

    const pdf = await page.pdf(createPdfOptions(options));

    if (options.output === "file" && options.outputPath) {
      return {
        path: options.outputPath
      };
    }

    return Buffer.from(pdf);
  } finally {
    await closePage(page).catch(() => undefined);
  }
}

export function renderPdf(options: RenderPdfFileOptions): Promise<RenderPdfFileResult>;
export function renderPdf(options: RenderPdfBufferOptions): Promise<Buffer>;
export function renderPdf(options: RenderPdfOptions): Promise<RenderPdfResult>;
export async function renderPdf(options: RenderPdfOptions): Promise<RenderPdfResult> {
  const normalizedOptions = validateOptions(options);

  try {
    return await renderWithBrowser(normalizedOptions);
  } catch (error) {
    if (error instanceof PdfEngineError) {
      throw error;
    }

    if (isTimeoutError(error)) {
      throw new PdfEngineError("TIMEOUT", "PDF rendering timed out.", {
        cause: error
      });
    }

    throw new PdfEngineError(
      "RENDER_FAILED",
      `Failed to render PDF: ${toErrorMessage(error)}`,
      {
        cause: error
      }
    );
  }
}
